import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faPen, faWarehouse, faGavel, faRoute, faGlobe, 
    faTruckRampBox, faTruckPickup, 
    faBoxesPacking, faForward, faBoxOpen, faParachuteBox, faUser, faTrashRestore, faCheck, 
    faCheckDouble, faCircleQuestion, faDollarSign, 
    faBox, faBoxes, faClock, faCube, faPallet, faGasPump, faTags, faHourglassHalf, faMoneyBill 
} from '@fortawesome/free-solid-svg-icons';
import { logout } from "../../redux/actions/authActions";

function PacketPulseHome() {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  return (
    <div>
      <div className="min-h-full bg-black">
        <header className="sticky mb-4 top-0 z-50 bg-black border-b border-blue-400 px-4 py-2 flex flex-row items-center justify-between shadow-lg">
          <div className='flex flex-col items-start justify-start'>
            <h2 className='text-lg lg:text-xl font-bold text-yellow-200 text-center mb-2'>
              <i className="fas fa-boxes-packing text-xl lg:text-2xl text-yellow-200"></i>packetPulse
            </h2>
            <h2 className='text-sm font-bold text-white text-center mb-2'>PacketPulse - Track Everything in Supply Chain</h2> 
          </div>  
          <div>
            <div className="hidden md:flex items-center gap-3">
              {isLoggedIn ? (
                <>
                  <div className="text-xs text-left text-blue-300">{userName}</div>
                  <button
                    onClick={handleLogout}
                    className="p-2 mt-2 rounded-md bg-blue-600 hover:bg-blue-700 transition"
                    title="Logout"
                  >
                    ⏻
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="px-3 py-1 rounded-full border border-blue-700 text-sm text-blue-200 hover:bg-blue-900">Login</Link>
                </>
              )}
            </div>
            <button
              onClick={() => setIsMobileMenuOpen((s) => !s)}
              className="md:hidden w-6 h-6 flex items-center justify-center text-xl text-blue-300 bg-blue-500"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? "×" : "☰"}
            </button>
          </div>
        </header>
        {isMobileMenuOpen && (
          <div className="md:hidden bg-[#2a0f2f] border-b border-blue-800 px-4 py-2 animate-fadeIn">
            {isLoggedIn ? (
              <div className="flex flex-row items-center justify-center gap-3">
                <span className="text-blue-300 text-xs lg:text-lg font-medium whitespace-nowrap h-7">{userName}</span>
                <button
                  onClick={handleLogout}
                  className="rounded-md bg-blue-600 hover:bg-blue-700 w-7 h-7 flex items-center justify-center text-white"
                  title="Logout"
                >
                  ⏻
                </button>
              </div>
            ) : (
              <div className="flex flex-row items-center justify-center gap-6">
                <Link to="/login" className="text-blue-300 hover:text-yellow-300">Login</Link>
              </div>
            )}
          </div>
        )}  
        <div className="min-h-[75vh] px-4">
            <div className="grid grid-cols-3 lg:grid-cols-5 gap-4 mb-4 border-b border-blue-400 py-2">
                <CardLink to="/packetpulse-create" icon={faPen} text="Prepare" />
                <CardLink to="/packetpulse-shelf" icon={faWarehouse} text="Shelf" />
                <CardLink to="/packetpulse-pick" icon={faTruckPickup} text="Pick" />
                <CardLink to="/packetpulse-pack" icon={faBoxesPacking} text="Pack" />
                <CardLink to="/packetpulse-dispatch" icon={faTruckRampBox} text="Dispatch" />
                <CardLink to="/packetpulse-delivery" icon={faGlobe} text="Delivery" />
                <CardLink to="/packetpulse-forward" icon={faForward} text="Forward" />
                <CardLink to="/packetpulse-unpack" icon={faBoxOpen} text="Unpack" />
                <CardLink to="/packetpulse-return" icon={faParachuteBox} text="Return" />
                <CardLink to="/packetpulse-consume" icon={faUser} text="Consume" />
                <CardLink to="/packetpulse-dispose" icon={faTrashRestore} text="Dispose" />
                <CardLink to="/packetpulse-quality-check" icon={faCheck} text="QC" />
                <CardLink to="/packetpulse-inspection" icon={faCheckDouble} text="Inspection" />
                <CardLink to="/packetpulse-legal" icon={faGavel} text="Legal" />
                <CardLink to="/packetpulse-rca" icon={faCircleQuestion} text="RCA" />
                <CardLink to="/packetpulse-packet-journey" icon={faRoute} text="Journey" />
            </div>
            <div className='py-1'><p className='text-lg lg:text-xl text-yellow-200'>Calculator</p></div>  
            <div className="grid grid-cols-3 lg:grid-cols-5 gap-4">
                <CardLink to="/packetpulse-freight-cost" icon={faDollarSign} text="Freight Shipping Cost" />
                <CardLink to="/dimcalculator" icon={faBox} text="Dimensional Weight (DIM)" />
                <CardLink to="/packetpulse-container-load" icon={faBoxes} text="Container Load" />
                <CardLink to="/packetpulse-transit-time" icon={faClock} text="Transit Time" />
                <CardLink to="/packetpulse-cbm" icon={faCube} text="CBM (Cubic Meter)" />
                <CardLink to="/packetpulse-pallet" icon={faPallet} text="Pallet" />
                <CardLink to="/packetpulse-fuel-surcharge" icon={faGasPump} text="Fuel Surcharge" />
                <CardLink to="/packetpulse-custom-duty" icon={faTags} text="Custom Duty & Tax" />
                <CardLink to="/packetpulse-lead-time" icon={faHourglassHalf} text="Lead Time" />
                <CardLink to="/packetpulse-currency-conversion" icon={faMoneyBill} text="Currency Conversion" />
            </div>
        </div>
      </div>
      <footer className="bg-black border-t border-blue-800 text-blue-300 py-4">
        <div className="mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-xs lg:text-sm">© 2025 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-xs lg:text-sm">
            <Link to="/about-us" className="text-white hover:text-blue-400">About</Link>
            <Link to="/privacy-policy" className="text-white hover:text-blue-400">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-white hover:text-blue-400">Terms of Use</Link>
            <Link to="/contact-us" className="text-white hover:text-blue-400">Contact</Link>
          </div>
        </div>
      </footer>      
    </div>
  );
}

// CardLink Component
const CardLink = ({ to, icon, text }) => (
  <div className="bg-white p-2 rounded-lg shadow-lg transition-transform transform hover:scale-105">
    <Link to={to} className="flex items-center text-sm sm:text-base text-gray-700 hover:text-gray-900">
      <FontAwesomeIcon icon={icon} className="text-lg mr-3" />
      {text}
    </Link>
  </div>
);

// CardNoLink Component
const CardNoLink = ({ icon, text }) => (
  <div className="bg-gray-300 p-2 rounded-lg shadow-lg transition-transform transform hover:scale-105 cursor-not-allowed opacity-75">
    <div className="flex items-center text-sm sm:text-base text-gray-700">
      <FontAwesomeIcon icon={icon} className="text-lg mr-3" />
      {text}
    </div>
  </div>
);

export default PacketPulseHome;
